
import { Component, Vue } from 'vue-property-decorator'
import { UserModule } from '@/store/modules/user'
import { MonitorService } from '@/services/index'
import { IMiniHealthData } from '@/types/monitor-types'

/** 迷你健康卡 - 展示结构（数值已格式化，可直接渲染） */
interface IMiniHealth {
    /** 是否存在待关注异常：true 红点呼吸 / false 绿点 */
    hasAbnormal: boolean
    /** 请求总量（万级，如 128.5 万） */
    requestTotal: string
    /** SSR 占比（如 71.4%） */
    ssrRatio: string
    /** 爬虫 × SSR 压力（万级，如 17.3 万） */
    botSSR: string
    /** 慢接口数 */
    slowApi: string
    /** 违规用户数 */
    violationUsers: string
}

@Component({
    name: 'MonitorEntry'
})
export default class MonitorEntry extends Vue {
    /** 弹窗显隐 */
    private popupVisible: boolean = false

    /** 弹窗宿主（挂在 document.body 下） */
    private popupHost: HTMLElement | null = null

    /** 当前展示的弹窗节点（用于定位 / 动画） */
    private popupEl: HTMLElement | null = null

    /** 迷你健康卡展示数据：接口返回前 / 失败时统一显示「--」占位 */
    private miniHealth: IMiniHealth = {
        hasAbnormal: false,
        requestTotal: '--',
        ssrRatio: '--',
        botSSR: '--',
        slowApi: '--',
        violationUsers: '--'
    }

    /** 仅管理员及以上可见：role 1 管理员 / 2 超管 */
    get isAdmin(): boolean {
        return UserModule.role === 1 || UserModule.role === 2
    }

    private mounted() {
        // 1) 在 document.body 下挂一个宿主容器，所有弹窗 DOM 都长在这里
        const host = document.createElement('div')
        host.className = 'pdm-mini-health-host'
        // 在宿主上阻断 click，避免触发到外面的「点外部关闭」监听
        host.addEventListener('click', this.blockClick)
        document.body.appendChild(host)
        this.popupHost = host

        // 2) 拉取迷你健康卡数据（失败时保留「--」占位，不打扰用户）
        this.fetchMiniHealth()

        // 3) 视图尺寸变化时跟随重定位
        window.addEventListener('resize', this.handleViewportChange, true)
        window.addEventListener('scroll', this.handleViewportChange, true)

        // 4) 点其他位置关闭弹窗
        document.addEventListener('click', this.handleDocClick)
    }

    private beforeDestroy() {
        const host = this.popupHost
        if (host && host.parentNode) {
            host.parentNode.removeChild(host)
        }
        this.popupHost = null
        this.popupEl = null
        window.removeEventListener('resize', this.handleViewportChange, true)
        window.removeEventListener('scroll', this.handleViewportChange, true)
        document.removeEventListener('click', this.handleDocClick)
    }

    private togglePopup() {
        this.popupVisible = !this.popupVisible
        if (this.popupVisible) {
            this.openPopup()
        } else {
            this.closePopup()
        }
    }

    private openPopup() {
        this.renderPopup()
        // 下一帧加 .pdm-show 触发进场动画
        requestAnimationFrame(() => {
            if (this.popupEl) this.popupEl.classList.add('pdm-show')
        })
    }

    /** 渲染 / 刷新弹窗内容（数据更新时可重复调用，不重播动画） */
    private renderPopup() {
        const host = this.popupHost
        if (!host) return

        host.innerHTML = this.buildPopupHTML()
        this.popupEl = host.querySelector('.mini-popup') as HTMLElement
        const enterBtn = host.querySelector('.mini-enter') as HTMLElement
        if (enterBtn) {
            enterBtn.addEventListener('click', this.handleEnterClick)
        }

        this.reposition()
    }

    private closePopup() {
        const host = this.popupHost
        const el = this.popupEl
        if (!host || !el) return
        el.classList.remove('pdm-show')
        setTimeout(() => {
            // 动画结束后清空 DOM，避免长期占位
            if (el.parentNode === host) {
                host.innerHTML = ''
            }
        }, 200)
        this.popupEl = null
    }

    private handleDocClick = () => {
        if (this.popupVisible) {
            this.popupVisible = false
            this.closePopup()
        }
    }

    private handleEnterClick = () => {
        this.popupVisible = false
        this.closePopup()
        // P2 路由接入后会跳到 /monitor/dashboard
        this.$router.push('/monitor/dashboard').catch(() => { /* 路由未注册，吞掉导航错误 */ })
    }

    private handleViewportChange = () => {
        if (this.popupVisible) this.reposition()
    }

    /**
     * 拉取迷你健康卡数据：GET /v1/monitor/mini-health
     * status 非 200（如 421）时 data 为 null → 静默保留「--」占位
     */
    private async fetchMiniHealth() {
        try {
            const result = await MonitorService.getMiniHealth()
            if (result.status !== 200 || !result.data) return

            const d = result.data as IMiniHealthData
            this.miniHealth = {
                hasAbnormal: !!d.has_abnormal,
                requestTotal: this.toWan(d.request_total),
                ssrRatio: this.toPercent(d.ssr_ratio),
                botSSR: this.toWan(d.bot_ssr_count),
                slowApi: this.toPlain(d.slow_api_count),
                violationUsers: this.toPlain(d.violation_user_count)
            }

            // 弹窗已打开时刷新内容，并补回 show 类避免闪烁
            if (this.popupVisible) {
                this.renderPopup()
                if (this.popupEl) {
                    this.popupEl.classList.add('pdm-show')
                }
            }
        } catch (e) {
            // 网络异常由 xhr/common-handler 统一提示，此处静默
        }
    }

    /** 万级格式化：>= 10000 → 「x.x 万」，否则原整数 */
    private toWan(n: number): string {
        const v = Number(n)
        if (!isFinite(v)) return '--'
        if (v < 10000) return String(Math.round(v))
        return (v / 10000).toFixed(1) + ' 万'
    }

    /** 占比格式化：0~1 → 「xx.x%」 */
    private toPercent(n: number): string {
        const v = Number(n)
        if (!isFinite(v)) return '--'
        return (v * 100).toFixed(1) + '%'
    }

    /** 计数格式化 */
    private toPlain(n: number): string {
        const v = Number(n)
        if (!isFinite(v)) return '--'
        return String(Math.round(v))
    }

    /** 根据按钮的 viewport rect 重新定位弹窗 */
    private reposition() {
        const el = this.popupEl
        const btn = this.$refs.buttonEl as HTMLElement | undefined
        if (!el || !btn) return
        const rect = btn.getBoundingClientRect()
        el.style.top = (rect.bottom + 6) + 'px'
        el.style.right = (window.innerWidth - rect.right) + 'px'
    }

    private blockClick = (e: Event) => e.stopPropagation()

    /** 直接拼 HTML（mock 数据为静态字符串，暂不需要 Vue 响应式） */
    private buildPopupHTML(): string {
        const h = this.miniHealth
        const esc = (v: any) => String(v)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
        return `
            <div class="mini-popup">
                <h4 class="mini-title"><i class="mini-title__dot"></i>系统健康 · 今日</h4>
                <div class="mini-row">
                    <span class="mini-row__label">请求总量</span>
                    <span class="mini-row__value">${esc(h.requestTotal)}</span>
                </div>
                <div class="mini-row">
                    <span class="mini-row__label">SSR 占比</span>
                    <span class="mini-row__value mini-row__value--ok">${esc(h.ssrRatio)}</span>
                </div>
                <div class="mini-row">
                    <span class="mini-row__label">爬虫 × SSR</span>
                    <span class="mini-row__value mini-row__value--hot">${esc(h.botSSR)}</span>
                </div>
                <div class="mini-row">
                    <span class="mini-row__label">慢接口</span>
                    <span class="mini-row__value mini-row__value--hot">${esc(h.slowApi)}</span>
                </div>
                <div class="mini-row mini-row--last">
                    <span class="mini-row__label">违规用户</span>
                    <span class="mini-row__value mini-row__value--hot">${esc(h.violationUsers)}</span>
                </div>
                <button class="mini-enter" type="button">进入技术后台 →</button>
            </div>
        `
    }
}
